'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import {
  toBrandProductListPage,
  toCategoryListPage,
  toSearchListPage,
} from '@/feature/product-list/services/productList.routing'
import { PRODUCT_IMAGE_SIZE_THUMBNAIL } from '@/feature/product/services/product.constants'
import { toSearchProductPage } from '@/feature/product/services/product.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { ensureDecodedURIComponent } from '@/lib/typescript/uriUtils'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Overlay } from '@/ui/layouts/overlay/Overlay'
import { Link } from '@/ui/links/link/Link.client'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { Skin } from '@/ui/skin/Skin'
import { HighlightedText } from '@/ui/text/highlighted-text/HighlightedText.client'
import { Title } from '@/ui/text/title/Title'
import type { SearchProductsField_SearchFragment, SearchQuery } from '@gql-generated/graphql'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFocusRing } from 'react-aria'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { useDebounceValue } from 'usehooks-ts'
import { SearchInputButtons } from './_components/SearchInputButtons.client'

// #region Variable and Types ------------------------------

export const MINIMUM_CHARACTER_SEARCH_TRIGGER = 2
const HIGHLIGHT_CASE_SENSITIVE = false
const HIGHLIGHT_ACCENT_SENSITIVE = false
const HIGHLIGHT_COLOR = 'primary'

export const DEBOUNCE_TIMING = 200

export type SearchInputProps = WithClassName & {
  /** The submit event for the SearchBar */
  onSubmit: (search: string) => void

  /** If the SearchBar is loading */
  loading: boolean

  /** All the results of the search */
  results?: SearchQuery['search']

  /** Is there any errors on the search part ? */
  error?: boolean

  /** The submit event for the SearchBar */
  onSubmitAnalytics: (search: string) => void
}

export type SearchInputItemProps = {
  /** The sent product */
  product: NonNullable<SearchProductsField_SearchFragment['products']>[number]

  /** The search sent to the server */
  search: string

  /** The text sent for the reference of the found products */
  refText: string

  /** Callback when the link is clicked */
  onPress?: () => void
}

// #endregion

// #region ProductSearchInputItem ------------------------------

export function ProductSearchInputItem({
  product,
  search,
  refText,
  onPress,
}: SearchInputItemProps): React.ReactElement | null {
  if (!product?.urlKey || !product.name) {
    return null
  }

  return (
    <div>
      <Link
        variant="free"
        underlined="hover"
        href={toSearchProductPage(product.reference)}
        className="flex cursor-pointer flex-row gap-2 px-3 py-2 leading-light"
        onPress={onPress}
      >
        <CloudflarePictureProduct
          className="size-[3.375rem]"
          src={product.mainImage?.set?.[0]?.url}
          alt={product.mainImage?.alt || product.name || ''}
          mode="contain"
          width={PRODUCT_IMAGE_SIZE_THUMBNAIL}
        />
        <div className="flex flex-col justify-center gap-0.5">
          {product.brand && !!product.brand.name.length && (
            <HighlightedText
              highlight={search}
              caseSensitive={HIGHLIGHT_CASE_SENSITIVE}
              accentSensitive={HIGHLIGHT_ACCENT_SENSITIVE}
              color={HIGHLIGHT_COLOR}
              className="font-bold"
            >
              {product.brand.name}
            </HighlightedText>
          )}
          <HighlightedText
            highlight={search}
            caseSensitive={HIGHLIGHT_CASE_SENSITIVE}
            accentSensitive={HIGHLIGHT_ACCENT_SENSITIVE}
            color={HIGHLIGHT_COLOR}
            className="line-clamp-2"
          >
            {product.name}
          </HighlightedText>
          <p className="text-sm">{`${refText}: ${product.reference}`}</p>
        </div>
      </Link>
    </div>
  )
}

// #endregion

// #region ProductSearchInput ------------------------------

const searchBarStyle =
  'cursor-text outline-none rounded-full hd:border-1.5 border-2 border-solid border-brand-2-50 bg-brand-2-50 h-7 flex items-center px-2 [padding-inline-end:2rem] desktop:[padding-inline-end:3rem]'

export function ProductSearchInput({
  loading,
  results,
  error,
  className,
  ...props
}: SearchInputProps): React.ReactElement {
  /** The state connected to the input */
  const [search, setSearch] = useState('')

  /** The input value, with a debounce */
  const [debouncedSearch] = useDebounceValue(search, DEBOUNCE_TIMING)

  /** Is the Overlay on SearchBar is Visible */
  const [displayOverlay, setDisplayOverlay] = useState(false)

  /** The input as a ref, to blur or focus when changing the modal */
  const inputRef = useRef<HTMLInputElement>(null)

  /** The fake input, to position the real input when opened */
  const fakeInputRef = useRef<HTMLInputElement>(null)

  /** The loading, with the debounce behavior inside */
  const expandedLoading = useMemo<boolean>(
    () => loading || search !== debouncedSearch,
    [debouncedSearch, loading, search],
  )

  /** Whether the search button should be disabled */
  const isSearchDisabled = search.length > 0 && search.length < MINIMUM_CHARACTER_SEARCH_TRIGGER

  const { t } = useTranslation(['searchInput', 'generic'])
  const { onSubmit, onSubmitAnalytics } = props

  /** The texts of the SearchBar, ready for translation */
  const {
    productsTitle,
    productsSeeAllText,
    suggestionsText,
    categoriesText,
    brandsText,
    refText,
    placeholder,
  } = useMemo(
    () => ({
      productsTitle: t('searchInput:productsTitle'),
      productsSeeAllText: t('searchInput:productsSeeAllText'),
      suggestionsText: t('searchInput:suggestionsText'),
      categoriesText: t('searchInput:categoriesText'),
      brandsText: t('searchInput:brandsText'),
      refText: t('searchInput:refText'),
      placeholder: t('searchInput:placeholder'),
    }),
    [t],
  )

  const closeOverlay = useCallback(() => {
    setDisplayOverlay(false)
    inputRef.current?.blur()
  }, [])

  // #region Search Behavior --------------------

  const router = useRouter()
  const pathname = usePathname()

  const onFormSubmit = useCallback(() => {
    if (search.split(/\S/g).length - 1 < MINIMUM_CHARACTER_SEARCH_TRIGGER) return

    onSubmitAnalytics(search)

    router.push(toSearchListPage(ensureDecodedURIComponent(search)))

    closeOverlay()
  }, [router, search, closeOverlay, onSubmitAnalytics])

  const onInputFocus = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.setSelectionRange(0, inputRef.current.value.length)
    }
  }, [])

  /**
   * Reset search input and close overlay when the user navigates to another page.
   */
  useEffect(() => {
    setSearch('')
    closeOverlay()
  }, [pathname, closeOverlay])

  // #endregion

  // #region Left Sections ------------------------------

  const leftPanelLinkProps = useMemo(
    () =>
      ({
        className: 'py-0.5 block cursor-pointer leading-light',
        tint: 'brand-2',
        underlined: 'hover',
      }) as const,
    [],
  )

  const shouldDisplayLeftSection =
    results &&
    results.suggestions &&
    results.categories &&
    results.brands &&
    (results.suggestions.length > 0 || results.categories.length > 0 || results.brands.length > 0)

  // #endregion

  // #region Product Section ------------------------------

  const noResults = useMemo(
    () =>
      !results ||
      (!results.brands?.length &&
        !results.categories?.length &&
        !results.products?.length &&
        !results.suggestions?.length),
    [results],
  )

  const isResultTabDisplayable = useMemo(() => {
    const hasSearch = search.length >= MINIMUM_CHARACTER_SEARCH_TRIGGER
    const isDisplayable = hasSearch && !error && !noResults

    return isDisplayable
  }, [search.length, error, noResults])

  useEffect(() => {
    onSubmit(debouncedSearch)
  }, [onSubmit, debouncedSearch])

  // #endregion

  // #region return --------------------------------

  const searchLink = toSearchListPage(ensureDecodedURIComponent(search))

  const openSearchOverlay = useCallback(() => setDisplayOverlay(true), [])

  useEffect(() => {
    if (displayOverlay) {
      inputRef.current?.focus()
    }
  }, [displayOverlay])

  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onFormSubmit()
      }}
      className={className}
      data-testid="search-input"
    >
      <div className="relative flex flex-row items-center">
        <div
          ref={fakeInputRef}
          tabIndex={0}
          role="button"
          {...focusProps}
          data-focus-visible={isFocusVisible || undefined}
          className={twMerge(
            'w-full',
            searchBarStyle,
            'outline-none outline-2 outline-offset-2 data-[focus-visible]:outline-brand-1-800',
          )}
          data-testid="search-input/input-button"
          onClick={(e) => {
            e.preventDefault()
            openSearchOverlay()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openSearchOverlay()
            }
          }}
        >
          <div className="overflow-hidden">
            {search !== '' && !displayOverlay ? (
              search
            ) : (
              <p className="whitespace-nowrap text-lg opacity-40">{placeholder}</p>
            )}
          </div>
        </div>
        <SearchInputButtons
          buttonSize="md"
          isLoading={!displayOverlay ? expandedLoading : false}
          isSearchButtonDisabled={!displayOverlay && isSearchDisabled}
          onResetAction={() => setSearch('')}
          onSearchAction={onFormSubmit}
          shouldShowReset={!displayOverlay && search !== ''}
          value={!displayOverlay ? search : ''}
          skinTint="body-alt"
        />
      </div>
      <ModalOverlay
        className="fixed top-0 z-search-bar h-dvh w-full"
        isOpen={displayOverlay}
        isDismissable
        onOpenChange={(open) => {
          if (open === false) {
            setDisplayOverlay(false)
          }
        }}
      >
        <Modal>
          <Dialog
            aria-label={t('searchInput:searchResults')}
            className="absolute left-0 z-search-bar flex h-screen w-full flex-col outline-none desktop:pointer-events-none desktop:pb-4"
          >
            <div className="bg-white py-2 pl-1 pr-2 desktop:mx-auto desktop:w-full desktop:max-w-[1200px] desktop:bg-clear desktop:px-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onFormSubmit()
                }}
                className="relative flex flex-row items-center gap-1 desktop:pointer-events-auto desktop:pt-[--app-banner-height]"
              >
                <div className="block desktop:hidden">
                  <Button
                    size="md"
                    iconLeft={<Icon.ChevronLeft />}
                    variant="quaternary"
                    isRound
                    isLoading={expandedLoading}
                    aria-label={t('searchInput:hideSearch')}
                    onPress={() => {
                      setDisplayOverlay(false)
                    }}
                  />
                </div>
                <input
                  name="search-input"
                  ref={inputRef}
                  onChange={({ target }) => {
                    setSearch(target.value)
                  }}
                  onFocus={onInputFocus}
                  value={search}
                  // FIXME: replace focus: by useFocus()
                  className={twMerge(
                    // eslint-disable-next-line dc/no-focus-visible
                    '!h-8 w-full text-lg focus-visible:border-brand-2-200',
                    searchBarStyle,
                  )}
                  data-testid="search-input/input"
                  placeholder={placeholder}
                />
                <SearchInputButtons
                  buttonSize="md"
                  isLoading={expandedLoading}
                  isSearchButtonDisabled={isSearchDisabled}
                  onResetAction={() => setSearch('')}
                  onSearchAction={onFormSubmit}
                  shouldShowReset={search !== ''}
                  value={search}
                  skinTint="body-alt"
                />
              </form>
            </div>

            {results !== undefined && isResultTabDisplayable && (
              <div className="overflow-x-auto text-brand-2-900 desktop:mx-auto desktop:w-full desktop:max-w-[1200px] desktop:overflow-hidden desktop:px-4">
                <div className="relative flex w-full flex-col gap-2 bg-white p-2 desktop:pointer-events-auto desktop:h-full desktop:flex-row desktop:rounded-md">
                  {shouldDisplayLeftSection && (
                    <div className="flex min-w-[300px] grow flex-col gap-1 overflow-x-auto">
                      {/* Suggestions Part */}
                      {results.suggestions && results.suggestions.length > 0 && (
                        <Skin tint="body-alt">
                          <div className="rounded-sm p-2">
                            <Title as="h3" className="pb-1 font-bold" size="xl">
                              {suggestionsText}
                            </Title>
                            <CartItemList>
                              {results.suggestions.map((suggestion, index) => {
                                const suggestionLink = toSearchListPage(
                                  ensureDecodedURIComponent(suggestion.label),
                                )

                                return (
                                  <CartItemList.Item key={suggestion.label}>
                                    <Link
                                      variant="free"
                                      href={suggestionLink}
                                      onPress={() => {
                                        setDisplayOverlay(false)
                                        setSearch(suggestion.label)
                                        pushToGtmDataLayer({
                                          event: 'search',
                                          eventCategory: 'Recherche Interne - Layer de suggestion',
                                          eventAction: search,
                                          eventLabel: `Suggestions | ${suggestion.label} | ${index + 1}`,
                                        })
                                      }}
                                      {...leftPanelLinkProps}
                                    >
                                      <HighlightedText
                                        highlight={search}
                                        caseSensitive={HIGHLIGHT_CASE_SENSITIVE}
                                        accentSensitive={HIGHLIGHT_ACCENT_SENSITIVE}
                                        color={HIGHLIGHT_COLOR}
                                      >
                                        {suggestion.label}
                                      </HighlightedText>
                                    </Link>
                                  </CartItemList.Item>
                                )
                              })}
                            </CartItemList>
                          </div>
                        </Skin>
                      )}
                      {/* Categories Part */}
                      {results.categories && results.categories.length > 0 && (
                        <Skin tint="body-alt">
                          <div className="rounded-sm p-2">
                            <Title as="h3" className="pb-1 font-bold" size="xl">
                              {categoriesText}
                            </Title>
                            <CartItemList>
                              {results.categories.map((category, index) => {
                                const categoryLink = toCategoryListPage(category.urlKey)

                                return (
                                  <CartItemList.Item key={category.urlKey}>
                                    <Link
                                      variant="free"
                                      href={categoryLink}
                                      onPress={() => {
                                        setDisplayOverlay(false)
                                        pushToGtmDataLayer({
                                          event: 'search',
                                          eventCategory: 'Recherche Interne - Layer de suggestion',
                                          eventAction: search,
                                          eventLabel: `Categories | ${category.name} | ${index + 1}`,
                                        })
                                      }}
                                      {...leftPanelLinkProps}
                                    >
                                      <HighlightedText
                                        highlight={search}
                                        caseSensitive={HIGHLIGHT_CASE_SENSITIVE}
                                        accentSensitive={HIGHLIGHT_ACCENT_SENSITIVE}
                                        color={HIGHLIGHT_COLOR}
                                      >
                                        {category.name}
                                      </HighlightedText>
                                    </Link>
                                  </CartItemList.Item>
                                )
                              })}
                            </CartItemList>
                          </div>
                        </Skin>
                      )}
                      {/* Brands Part */}
                      {results.brands && results.brands.length > 0 && (
                        <Skin tint="body-alt">
                          <div className="rounded-sm p-2">
                            <Title as="h3" className="pb-1 font-bold" size="xl">
                              {brandsText}
                            </Title>
                            <CartItemList>
                              {results.brands.map((brand, index) => {
                                const brandLink = toBrandProductListPage(brand.urlKey || '#')

                                return (
                                  <CartItemList.Item key={brand.urlKey}>
                                    <Link
                                      variant="free"
                                      href={brandLink}
                                      onPress={() => {
                                        setDisplayOverlay(false)
                                        pushToGtmDataLayer({
                                          event: 'search',
                                          eventCategory: 'Recherche Interne - Layer de suggestion',
                                          eventAction: search,
                                          eventLabel: `Brands | ${brand.name} | ${index + 1}`,
                                        })
                                      }}
                                      {...leftPanelLinkProps}
                                    >
                                      <HighlightedText
                                        highlight={search}
                                        caseSensitive={HIGHLIGHT_CASE_SENSITIVE}
                                        accentSensitive={HIGHLIGHT_ACCENT_SENSITIVE}
                                        color={HIGHLIGHT_COLOR}
                                      >
                                        {brand.name}
                                      </HighlightedText>
                                    </Link>
                                  </CartItemList.Item>
                                )
                              })}
                            </CartItemList>
                          </div>
                        </Skin>
                      )}
                    </div>
                  )}
                  {!!results?.products?.length && (
                    <div className="flex w-full flex-col gap-1 desktop:h-full">
                      <div className="flex w-full flex-row items-center justify-between rounded-sm p-2">
                        <Title as="h2" className="text-xl font-bold">
                          {productsTitle}
                        </Title>
                        <Link
                          variant="free"
                          underlined="hover"
                          bold
                          href={searchLink}
                          onPress={() => {
                            setDisplayOverlay(false)
                            pushToGtmDataLayer({
                              event: 'search',
                              eventCategory: 'Recherche Interne - Layer de suggestion',
                              eventAction: search,
                              eventLabel: 'clic voir tout',
                            })
                          }}
                        >
                          {productsSeeAllText}
                        </Link>
                      </div>
                      <CartItemList withDividers className="overflow-x-auto">
                        {results.products.map(
                          (product, index) =>
                            product.urlKey && (
                              <CartItemList.Item key={product.urlKey}>
                                <ProductSearchInputItem
                                  search={debouncedSearch}
                                  product={product}
                                  refText={refText}
                                  onPress={() => {
                                    setDisplayOverlay(false)
                                    pushToGtmDataLayer({
                                      event: 'search',
                                      eventCategory: 'Recherche Interne - Layer de suggestion',
                                      eventAction: search,
                                      eventLabel: `Products | ${product.name} | ${index + 1}`,
                                    })
                                  }}
                                />
                              </CartItemList.Item>
                            ),
                        )}
                      </CartItemList>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Dialog>
        </Modal>
        <Overlay
          isVisible={displayOverlay}
          className="absolute top-0 z-search-bar-background size-full"
          onClick={closeOverlay}
        />
      </ModalOverlay>
    </form>
  )

  // #endregion
}

// #endregion
